import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';
import { qualityKeys } from '@/lib/api/quality.js';
import {
  developerByWorkspaceKey,
  type DeveloperType,
} from '@/lib/api/developers.js';

/**
 * Client for the workspace registry (`cti-workspace-service`, `/v1/workspaces`). Global-workspaces
 * phase 2: the registry, the membership directory (unified ACL), and workspace sections. REST is
 * snake_case on the wire; this maps it to the camelCase types the SPA uses. When a field crosses this
 * boundary the exact snake_case key must match the service DTO — the service's `ValidationPipe`
 * silently drops any key it doesn't declare.
 */

const API_BASE = '/v1/workspaces';

export type WorkspaceStatus = 'active' | 'archived';
export type WorkspacePrivacy = 'private' | 'org';
/** `personal` = the user's auto-provisioned private workspace (one per user); `shared` = every other. */
export type WorkspaceKind = 'personal' | 'shared';
/** Membership roles (C2/C3). `editor` is the runner role; `view_all_runs` is an orthogonal flag. */
export type WorkspaceRole = 'owner' | 'editor' | 'viewer';

/** A workspace as the SPA consumes it (camelCase). */
export interface Workspace {
  id: string;
  orgId: string | null;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  status: WorkspaceStatus;
  privacy: WorkspacePrivacy;
  kind: WorkspaceKind;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * The caller's pin position in the switcher, null when unpinned. Undefined when the service does
   * not send `pinned_position` yet, which hides the pin controls during a rolling deploy.
   */
  pinnedPosition?: number | null;
}

/** The snake_case shape the service returns. */
interface WorkspaceWire {
  id: string;
  org_id: string | null;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  status: WorkspaceStatus;
  privacy: WorkspacePrivacy;
  /** `.optional()` in practice for the rolling-deploy window; defaults to `shared` when absent. */
  kind?: WorkspaceKind;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  /** Sent on `GET /v1/workspaces` items only. */
  pinned_position?: number | null;
}

/** A registered workspace domain (from `GET /v1/workspaces/:id?expand=...`). */
export interface WorkspaceDomain {
  domain: string;
  enabled: boolean;
}

/**
 * A workspace member as the SPA consumes it. The public directory
 * (`GET /v1/workspaces/:id/members`) and the mutation responses (add/change) both carry
 * `viewAllRuns`/`grantedBy`/`grantedByName`; they stay optional for the rolling-deploy window (an
 * older directory response omits them, so the Access-tab toggle simply hides until they arrive).
 */
export interface WorkspaceMember {
  userId: string;
  /**
   * The member's Entra object id, when the directory carries it. A second id space, not a duplicate of
   * `userId`: `userId` is the internal identity id the ACL keys on, while a chat message's
   * `sender_user_id` is stamped as `oid ?? internal id`, so resolving a speaker's name needs the oid to
   * match an SSO sender (see `ui/chat/speaker.ts`). Null when the member has no linked SSO identity, and
   * absent while the directory response does not carry the field yet.
   */
  oid?: string | null;
  name: string | null;
  email: string | null;
  role: WorkspaceRole;
  viewAllRuns?: boolean;
  grantedBy?: string | null;
  grantedByName?: string | null;
}

/**
 * Directory entry shape (public members endpoint). Phase 2 completion: the directory now also
 * carries `view_all_runs`/`granted_by`/`granted_by_name`, so the Access tab shows the correct toggle
 * state and "granted by" on the first load (not only after a mutation). Kept `?`-optional for the
 * rolling-deploy window — an older service that omits them still parses.
 */
interface MemberDirectoryWire {
  user_id: string;
  /** Entra object id of the member; `?`-optional — see `WorkspaceMember.oid`. */
  oid?: string | null;
  name: string | null;
  email: string | null;
  role: WorkspaceRole;
  view_all_runs?: boolean;
  granted_by?: string | null;
  granted_by_name?: string | null;
}

/** Full member shape (add/change mutation responses — `serializeMember`). */
interface MemberWire {
  workspace_id: string;
  user_id: string;
  role: WorkspaceRole;
  view_all_runs: boolean;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
  updated_at: string;
}

/** Add-member request body (snake_case wire). `owner` is not grantable — only editor/viewer. */
export interface AddMemberInput {
  userId: string;
  role: Exclude<WorkspaceRole, 'owner'>;
  viewAllRuns?: boolean;
}

/** Change-member request body — only present fields are applied server-side. */
export interface ChangeMemberInput {
  role?: WorkspaceRole;
  viewAllRuns?: boolean;
}

/** Create/patch payload (only the fields the create DTO accepts). */
export interface WorkspaceInput {
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  privacy?: WorkspacePrivacy;
}

function fromWire(wire: WorkspaceWire): Workspace {
  return {
    id: wire.id,
    orgId: wire.org_id,
    slug: wire.slug,
    name: wire.name,
    description: wire.description,
    icon: wire.icon,
    color: wire.color,
    status: wire.status,
    privacy: wire.privacy,
    kind: wire.kind ?? 'shared',
    createdBy: wire.created_by,
    createdByName: wire.created_by_name,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
    pinnedPosition: wire.pinned_position,
  };
}

function memberFromDirectory(wire: MemberDirectoryWire): WorkspaceMember {
  return {
    userId: wire.user_id,
    oid: wire.oid ?? null,
    name: wire.name,
    email: wire.email,
    role: wire.role,
    viewAllRuns: wire.view_all_runs,
    grantedBy: wire.granted_by ?? null,
    grantedByName: wire.granted_by_name ?? null,
  };
}

function memberFromWire(wire: MemberWire): WorkspaceMember {
  return {
    userId: wire.user_id,
    oid: null,
    name: null,
    email: null,
    role: wire.role,
    viewAllRuns: wire.view_all_runs,
    grantedBy: wire.granted_by,
    grantedByName: wire.granted_by_name,
  };
}

/** Content type only — `apiFetch` attaches the bearer from the renewal core. */
// eslint-disable-next-line @typescript-eslint/require-await
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json' };
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (${res.status})`;
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim())
      return body.message;
  } catch {
    /* not JSON */
  }
  return text.trim();
}

async function listWorkspaces(): Promise<Workspace[]> {
  const res = await apiFetch(API_BASE, { headers: await headers() });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load workspaces'));
  return ((await res.json()) as WorkspaceWire[]).map(fromWire);
}

/**
 * Replace the caller's pinned workspaces with `ids`, in that order. Pin, unpin and reorder all send the
 * whole list. Returns the ids the service stored, without any it dropped.
 */
async function setWorkspacePins(ids: string[]): Promise<string[]> {
  const res = await apiFetch(`${API_BASE}/pins`, {
    method: 'PUT',
    headers: await headers(),
    body: JSON.stringify({ workspace_ids: ids }),
  });
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to save pinned workspaces'),
    );
  return ((await res.json()) as { workspace_ids: string[] }).workspace_ids;
}

async function createWorkspace(input: WorkspaceInput): Promise<Workspace> {
  const res = await apiFetch(API_BASE, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(input),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to create workspace'));
  return fromWire((await res.json()) as WorkspaceWire);
}

async function updateWorkspace(args: {
  id: string;
  patch: Partial<WorkspaceInput> & { status?: WorkspaceStatus };
}): Promise<Workspace> {
  const res = await apiFetch(`${API_BASE}/${encodeURIComponent(args.id)}`, {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify(args.patch),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to update workspace'));
  return fromWire((await res.json()) as WorkspaceWire);
}

async function deleteWorkspace(id: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await headers(),
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(await errorMessage(res, 'Failed to delete workspace'));
  }
}

/** One workspace with its optional domain registry (`?expand=domains`). */
export interface WorkspaceWithDomains extends Workspace {
  domains?: WorkspaceDomain[];
}

export async function getWorkspace(id: string): Promise<WorkspaceWithDomains> {
  const res = await apiFetch(
    `${API_BASE}/${encodeURIComponent(id)}?expand=domains`,
    {
      headers: await headers(),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load workspace'));
  const wire = (await res.json()) as WorkspaceWire & {
    domains?: WorkspaceDomain[];
  };
  return { ...fromWire(wire), domains: wire.domains };
}

/**
 * Toggle a workspace domain (`PUT /v1/workspaces/:id/domains/:domain`). The body is `{ enabled }`;
 * the service echoes `{ domain, enabled }`. Enabling the `quality` domain also provisions the
 * workspace's quality project server-side (east-west), so the Quality tab can then resolve it;
 * disabling only flips the flag and keeps the data. Gated owner/editor by the service.
 */
async function setWorkspaceDomain(args: {
  id: string;
  domain: string;
  enabled: boolean;
  type?: DeveloperType;
}): Promise<WorkspaceDomain> {
  const res = await apiFetch(
    `${API_BASE}/${encodeURIComponent(args.id)}/domains/${encodeURIComponent(args.domain)}`,
    {
      method: 'PUT',
      headers: await headers(),
      /**
       * `type` is sent only when there is one, and only the `developer` domain ever has one: it
       * picks the kind of developer the enable provisions, and it can never be changed afterwards.
       * The key is omitted rather than sent as `undefined` because the service validates it.
       */
      body: JSON.stringify(
        args.type === undefined
          ? { enabled: args.enabled }
          : { enabled: args.enabled, type: args.type },
      ),
    },
  );
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to update workspace domain'),
    );
  const wire = (await res.json()) as WorkspaceDomain;
  return { domain: wire.domain, enabled: wire.enabled };
}

/** The workspace's agent instructions (Agent section). `null` when never set. */
export interface WorkspaceAgent {
  instructions: string | null;
}

/**
 * Load the workspace's agent instructions from the real shell endpoint
 * (`GET /v1/workspaces/:id/agent`). The service returns `{ instructions: string | null }`.
 */
async function getWorkspaceAgent(id: string): Promise<WorkspaceAgent> {
  const res = await apiFetch(`${API_BASE}/${encodeURIComponent(id)}/agent`, {
    headers: await headers(),
  });
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to load agent instructions'),
    );
  const wire = (await res.json()) as { instructions: string | null };
  return { instructions: wire.instructions ?? null };
}

/**
 * Save the workspace's agent instructions (`PATCH /v1/workspaces/:id/agent`). The body field is
 * literally `instructions`; an empty string clears them. The service echoes `{ instructions }`.
 */
export async function updateWorkspaceAgent(args: {
  id: string;
  instructions: string;
}): Promise<WorkspaceAgent> {
  const res = await apiFetch(
    `${API_BASE}/${encodeURIComponent(args.id)}/agent`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify({ instructions: args.instructions }),
    },
  );
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to save agent instructions'),
    );
  const wire = (await res.json()) as { instructions: string };
  return { instructions: wire.instructions };
}

async function listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const res = await apiFetch(
    `${API_BASE}/${encodeURIComponent(workspaceId)}/members`,
    {
      headers: await headers(),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load members'));
  return ((await res.json()) as MemberDirectoryWire[]).map(memberFromDirectory);
}

async function addMember(args: {
  workspaceId: string;
  input: AddMemberInput;
}): Promise<WorkspaceMember> {
  const body: Record<string, unknown> = {
    user_id: args.input.userId,
    role: args.input.role,
  };
  if (args.input.viewAllRuns !== undefined)
    body.view_all_runs = args.input.viewAllRuns;
  const res = await apiFetch(
    `${API_BASE}/${encodeURIComponent(args.workspaceId)}/members`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error(await errorMessage(res, 'Failed to add member'));
  return memberFromWire((await res.json()) as MemberWire);
}

async function changeMember(args: {
  workspaceId: string;
  userId: string;
  input: ChangeMemberInput;
}): Promise<WorkspaceMember> {
  const body: Record<string, unknown> = {};
  if (args.input.role !== undefined) body.role = args.input.role;
  if (args.input.viewAllRuns !== undefined)
    body.view_all_runs = args.input.viewAllRuns;
  const res = await apiFetch(
    `${API_BASE}/${encodeURIComponent(args.workspaceId)}/members/${encodeURIComponent(args.userId)}`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to update member'));
  return memberFromWire((await res.json()) as MemberWire);
}

async function removeMember(args: {
  workspaceId: string;
  userId: string;
}): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/${encodeURIComponent(args.workspaceId)}/members/${encodeURIComponent(args.userId)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(await errorMessage(res, 'Failed to remove member'));
  }
}

/** Query-key factory so mutations can invalidate precisely. */
export const workspaceKeys = {
  all: ['workspaces'] as const,
  detail: (id: string) => ['workspaces', 'detail', id] as const,
  members: (id: string) => ['workspaces', id, 'members'] as const,
  agent: (id: string) => ['workspaces', id, 'agent'] as const,
};

/** TanStack Query factory for the user's workspaces. */
export function workspacesQueryOptions() {
  return queryOptions({
    queryKey: workspaceKeys.all,
    queryFn: listWorkspaces,
    staleTime: 30_000,
  });
}

/** TanStack Query factory for one workspace (with its domain registry). */
export function workspaceQueryOptions(id: string) {
  return queryOptions({
    queryKey: workspaceKeys.detail(id),
    queryFn: () => getWorkspace(id),
    staleTime: 30_000,
  });
}

/** TanStack Query factory for a workspace's member directory. */
export function workspaceMembersQueryOptions(id: string) {
  return queryOptions({
    queryKey: workspaceKeys.members(id),
    queryFn: () => listMembers(id),
    staleTime: 30_000,
  });
}

/** TanStack Query factory for a workspace's agent instructions. */
export function workspaceAgentQueryOptions(id: string) {
  return queryOptions({
    queryKey: workspaceKeys.agent(id),
    queryFn: () => getWorkspaceAgent(id),
    staleTime: 30_000,
  });
}

export function useCreateWorkspace(): UseMutationResult<
  Workspace,
  Error,
  WorkspaceInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}

export function useUpdateWorkspace(): UseMutationResult<
  Workspace,
  Error,
  { id: string; patch: Partial<WorkspaceInput> & { status?: WorkspaceStatus } }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateWorkspace,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}

/** The cached list with each workspace's pin position taken from `ids`. */
function withPinPositions(workspaces: Workspace[], ids: string[]): Workspace[] {
  return workspaces.map((workspace) => {
    const position = ids.indexOf(workspace.id);
    return { ...workspace, pinnedPosition: position === -1 ? null : position };
  });
}

/** Marks every pin save, so saves run one at a time and each can tell whether another is pending. */
const pinSaveKey = [...workspaceKeys.all, 'pins'] as const;

/**
 * Save the caller's pin list. The switcher moves at once: the cached list takes the new positions
 * before the service replies. As with the dashboards reorder, the last save wins.
 *
 * The saves share one mutation scope, so each PUT waits until the one before it has settled. Two PUTs
 * in flight could reach the service out of order, and the older list would then replace the newer one.
 * The optimistic update still runs at once, because `onMutate` does not wait for the scope.
 *
 * Only the last save in flight touches the cache when it ends. An earlier save that reloaded the list,
 * or rolled it back, would wipe the newer optimistic pins, and the next click would then send a list
 * without them. So a save that ends while another is pending leaves the cache alone, and the last one
 * rolls back on failure and reloads the list.
 */
export function useSetWorkspacePins(): UseMutationResult<
  string[],
  Error,
  string[],
  { previous: Workspace[] | undefined }
> {
  const qc = useQueryClient();
  const isLastSave = (): boolean =>
    qc.isMutating({ mutationKey: pinSaveKey }) === 1;
  return useMutation({
    mutationKey: pinSaveKey,
    scope: { id: pinSaveKey.join(':') },
    mutationFn: setWorkspacePins,
    onMutate: async (ids: string[]) => {
      await qc.cancelQueries({ queryKey: workspaceKeys.all, exact: true });
      const previous = qc.getQueryData<Workspace[]>(workspaceKeys.all);
      if (previous) {
        qc.setQueryData(workspaceKeys.all, withPinPositions(previous, ids));
      }
      return { previous };
    },
    onError: (_error, _ids, context) => {
      if (context?.previous && isLastSave())
        qc.setQueryData(workspaceKeys.all, context.previous);
    },
    onSettled: () => {
      if (!isLastSave()) return;
      void qc.invalidateQueries({ queryKey: workspaceKeys.all, exact: true });
    },
  });
}

export function useDeleteWorkspace(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteWorkspace,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}

export function useAddMember(
  workspaceId: string,
): UseMutationResult<WorkspaceMember, Error, AddMemberInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddMemberInput) => addMember({ workspaceId, input }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workspaceKeys.members(workspaceId),
      });
    },
  });
}

export function useChangeMember(
  workspaceId: string,
): UseMutationResult<
  WorkspaceMember,
  Error,
  { userId: string; input: ChangeMemberInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: string; input: ChangeMemberInput }) =>
      changeMember({ workspaceId, userId: args.userId, input: args.input }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workspaceKeys.members(workspaceId),
      });
    },
  });
}

export function useRemoveMember(
  workspaceId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeMember({ workspaceId, userId }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workspaceKeys.members(workspaceId),
      });
    },
  });
}

/**
 * Toggle a workspace domain (e.g. `quality`). Invalidates the workspace detail so the domain's
 * enabled state re-reads, and the quality projects list because enabling provisions a project
 * server-side that the Quality tab must then resolve.
 */
export function useSetWorkspaceDomain(
  workspaceId: string,
): UseMutationResult<
  WorkspaceDomain,
  Error,
  { domain: string; enabled: boolean; type?: DeveloperType }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      domain: string;
      enabled: boolean;
      type?: DeveloperType;
    }) =>
      setWorkspaceDomain({
        id: workspaceId,
        domain: input.domain,
        enabled: input.enabled,
        type: input.type,
      }),
    onSuccess: (_domain, input) => {
      void qc.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
      void qc.invalidateQueries({ queryKey: qualityKeys.projects });
      /**
       * Disabling the `developer` domain deletes the developer, and the next enable makes a new one
       * under the same id (the workspace id). Everything cached under that id belongs to the deleted
       * one, so it goes now rather than being shown inside its stale window.
       */
      if (input.domain === 'developer' && !input.enabled) {
        qc.removeQueries({ queryKey: ['developers', workspaceId] });
      }
      /**
       * Enabling the `developer` domain **provisions** the workspace's developer, so the cached
       * answer to "does this workspace have one" is wrong the moment this succeeds. The Developer
       * tab asks that question while the domain is off — it is what decides whether the kind may
       * still be chosen — so a stale `null` there would offer the choice again for a workspace that
       * now has a developer, and send a type nothing can apply. Disabling deletes the developer, so
       * the same refresh brings the choice back for the next enable.
       */
      void qc.invalidateQueries({
        queryKey: developerByWorkspaceKey(workspaceId),
      });
    },
  });
}

/** Save the workspace's agent instructions; the mutation input is the raw instructions string. */
export function useUpdateWorkspaceAgent(
  workspaceId: string,
): UseMutationResult<WorkspaceAgent, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instructions: string) =>
      updateWorkspaceAgent({ id: workspaceId, instructions }),
    onSuccess: (agent) => {
      qc.setQueryData(workspaceKeys.agent(workspaceId), agent);
    },
  });
}
